import React, { useState, useEffect } from 'react';
import { Search, Filter, Pin, Archive, Edit2, Trash2, Tag, Calendar } from 'lucide-react';
import noteService from '../../services/noteService';

const NotesList = ({ onEdit, onRefresh }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalNotes, setTotalNotes] = useState(0);

  useEffect(() => {
    fetchNotes();
  }, [currentPage, categoryFilter, showArchived, onRefresh]);

  const fetchNotes = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page: currentPage,
        per_page: 12,
        archived: showArchived
      };
      
      if (categoryFilter) params.category = categoryFilter;
      if (searchTerm) params.search = searchTerm;
      
      const response = await noteService.getAllNotes(params);
      setNotes(response.data.notes);
      setTotalPages(response.data.pages);
      setTotalNotes(response.data.total);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load notes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchNotes();
  };

  const handleTogglePin = async (noteId) => {
    try {
      await noteService.togglePin(noteId);
      fetchNotes();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to toggle pin');
    }
  };

  const handleToggleArchive = async (noteId) => {
    try {
      await noteService.toggleArchive(noteId);
      fetchNotes();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to toggle archive');
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    
    try {
      await noteService.deleteNote(noteId);
      fetchNotes();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete note');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const truncateText = (text, maxLength = 150) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getCategoryColor = (category) => {
    const colors = {
      'General': 'bg-gray-100 text-gray-800',
      'Clinical': 'bg-blue-100 text-blue-800',
      'Research': 'bg-purple-100 text-purple-800',
      'Administration': 'bg-yellow-100 text-yellow-800',
      'Personal': 'bg-green-100 text-green-800',
      'Ideas': 'bg-pink-100 text-pink-800',
      'To-Do': 'bg-orange-100 text-orange-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="flex">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search notes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-indigo-600 text-white rounded-r-md hover:bg-indigo-700"
              >
                Search
              </button>
            </div>
          </div>

          {/* Category Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Categories</option>
              <option value="General">General</option>
              <option value="Clinical">Clinical</option>
              <option value="Research">Research</option>
              <option value="Administration">Administration</option>
              <option value="Personal">Personal</option>
              <option value="Ideas">Ideas</option>
              <option value="To-Do">To-Do</option>
            </select>
          </div>

          {/* Show Archived */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="showArchived"
              checked={showArchived}
              onChange={(e) => {
                setShowArchived(e.target.checked);
                setCurrentPage(1);
              }}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <label htmlFor="showArchived" className="ml-2 text-sm font-medium text-gray-700">
              Show Archived
            </label>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Notes Grid */}
      {notes.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center border border-gray-200">
          <p className="text-gray-500 text-lg">No notes found</p>
          <p className="text-gray-400 text-sm mt-2">Create your first note to get started!</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {notes.map((note) => (
              <div
                key={note.id}
                className={`bg-white rounded-lg shadow-md border hover:shadow-lg transition-shadow ${
                  note.is_pinned ? 'border-indigo-400 border-2' : 'border-gray-200'
                }`}
              >
                {/* Note Header */}
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        {note.is_pinned && (
                          <Pin className="w-4 h-4 text-indigo-600 fill-indigo-600" />
                        )}
                        <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">
                          {note.title}
                        </h3>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getCategoryColor(note.category)}`}>
                          {note.category}
                        </span>
                        <span className="text-xs text-gray-500 flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          {formatDate(note.updated_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Note Content */}
                <div className="p-4">
                  <p className="text-gray-700 text-sm whitespace-pre-wrap line-clamp-4">
                    {truncateText(note.content)}
                  </p>
                  
                  {/* Tags */}
                  {note.tags && note.tags.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {note.tags.slice(0, 3).map((tag, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                        >
                          <Tag className="w-3 h-3 mr-1" />
                          {tag}
                        </span>
                      ))}
                      {note.tags.length > 3 && (
                        <span className="text-xs text-gray-500">
                          +{note.tags.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Note Actions */}
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleTogglePin(note.id)}
                      className="text-gray-600 hover:text-indigo-600"
                      title={note.is_pinned ? 'Unpin' : 'Pin'}
                    >
                      <Pin className={`w-4 h-4 ${note.is_pinned ? 'fill-indigo-600 text-indigo-600' : ''}`} />
                    </button>
                    <button
                      onClick={() => handleToggleArchive(note.id)}
                      className="text-gray-600 hover:text-orange-600"
                      title={note.is_archived ? 'Unarchive' : 'Archive'}
                    >
                      <Archive className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onEdit(note)}
                      className="text-indigo-600 hover:text-indigo-900"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(note.id)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{((currentPage - 1) * 12) + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(currentPage * 12, totalNotes)}</span> of{' '}
                  <span className="font-medium">{totalNotes}</span> notes
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NotesList;